import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

/**
 * Stock Transfers — FR-INV-02.07 / 02.08
 * Two-step workflow: create draft → ship → receive (partial OK).
 */
export function TransfersPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['inv', 'transfers'],
    queryFn: () => inventoryApi.listTransfers({ limit: 500 }),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (listQuery.data ?? []) as any[];
    const k = {
      total: rows.length,
      drafts: rows.filter((r) => r.status === 'draft').length,
      inTransit: rows.filter((r) => r.status === 'in_transit').length,
      received: rows.filter((r) => r.status === 'received').length,
    };
    const filtered = statusFilter ? rows.filter((r) => r.status === statusFilter) : rows;
    return { kpis: k, filteredRows: filtered };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'in_transit',label: 'In transit' },
    { value: 'received',  label: 'Received' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Stock transfers"
        subtitle="Move inventory between warehouses. Two-step ship-then-receive with journal entries at both ends."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New transfer
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total" value={kpis.total} />
        <KpiTile label="Drafts" value={kpis.drafts} />
        <KpiTile label="In transit" value={kpis.inTransit}
          accent={kpis.inTransit > 0 ? 'amber' : 'default'} />
        <KpiTile label="Received" value={kpis.received} accent="sage" />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Two-pane */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_28rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message="No transfers"
              hint={statusFilter
                ? `No transfers in "${statusFilter.replace('_', ' ')}" status.`
                : 'Move stock between warehouses to keep locations balanced.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th className="text-right">Lines</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((t: any) => (
                    <tr
                      key={t.id}
                      onClick={() => setSelected(t.id)}
                      className={clsx(
                        'cursor-pointer',
                        selected === t.id && '!bg-canvas-subtle'
                      )}
                    >
                      <td className="font-mono text-xs font-semibold text-ink-900">
                        {t.transfer_number}
                      </td>
                      <td>
                        <div className="inline-flex items-center gap-1.5 text-sm">
                          <span className="font-mono text-ink-700 font-semibold">{t.from_warehouse_code}</span>
                          <ArrowRight />
                          <span className="font-mono text-ink-700 font-semibold">{t.to_warehouse_code}</span>
                        </div>
                      </td>
                      <td><TransferStatusBadge status={t.status} /></td>
                      <td className="text-right text-sm text-ink-700 font-mono tabular-nums">{t.line_count}</td>
                      <td className="text-sm text-ink-500">
                        {new Date(t.transfer_date).toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a transfer"
              hint="Click a row on the left to view lines and take action."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="7 13 12 18 17 13"/>
                  <polyline points="7 6 12 11 17 6"/>
                </svg>
              }
            />
          ) : (
            <TransferDetail
              transferId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['inv', 'transfers'] })}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateTransferModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['inv', 'transfers'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function TransferStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'received'  ? 'sage' :
    status === 'in_transit'? 'warning' :
    status === 'cancelled' ? 'danger' :
                             'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// TransferDetail
// ============================================================
function TransferDetail({ transferId, onChanged }: { transferId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['inv', 'transfer', transferId],
    queryFn: () => inventoryApi.getTransfer(transferId),
  });

  const shipMut = useMutation({
    mutationFn: () => inventoryApi.shipTransfer(transferId),
    onSuccess: (data) => {
      toast.success(`Shipped — JE ${data.journalEntryNumber ?? 'n/a'}, $${data.shippedValue?.toFixed(2)} in transit`);
      qc.invalidateQueries({ queryKey: ['inv', 'transfer', transferId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (detailQuery.isLoading) return <LoadingState rows={4} />;
  if (detailQuery.error) return <ErrorState error={detailQuery.error} />;
  const t = detailQuery.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{t.transfer_number}</div>
          <div className="inline-flex items-center gap-2 font-display font-bold text-lg text-ink-900">
            <span className="font-mono">{t.from_warehouse_code}</span>
            <ArrowRight />
            <span className="font-mono">{t.to_warehouse_code}</span>
          </div>
          {t.notes && (
            <div className="text-xs text-ink-500 italic mt-1">{t.notes}</div>
          )}
        </div>
        <TransferStatusBadge status={t.status} />
      </div>

      {/* Timeline */}
      <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1.5 text-xs text-ink-600">
        <div>
          <span className="text-ink-400">Created:</span>{' '}
          {new Date(t.created_at).toLocaleString()} · {t.created_by_name}
        </div>
        {t.shipped_at && (
          <div>
            <span className="text-ink-400">Shipped:</span>{' '}
            {new Date(t.shipped_at).toLocaleString()} · {t.shipped_by_name}
          </div>
        )}
        {t.received_at && (
          <div className="text-sage-700">
            <span className="text-sage-600">Received:</span>{' '}
            {new Date(t.received_at).toLocaleString()} · {t.received_by_name}
          </div>
        )}
        {t.ship_journal_entry_number && (
          <div className="text-2xs text-ink-400 font-mono pt-1">
            Ship JE: {t.ship_journal_entry_number}
            {t.receipt_journal_entry_number && ` · Receipt JE: ${t.receipt_journal_entry_number}`}
          </div>
        )}
      </div>

      {/* Lines */}
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">Shipped</th>
              <th className="text-right">Received</th>
            </tr>
          </thead>
          <tbody>
            {t.lines.map((l: any) => {
              const shipped = parseFloat(l.quantity);
              const received = parseFloat(l.received_qty);
              const open = shipped - received;
              return (
                <tr key={l.id}>
                  <td>
                    <div className="font-medium text-ink-900">{l.product_name}</div>
                    <div className="font-mono text-2xs text-ink-500">{l.sku}</div>
                  </td>
                  <td className="text-right font-mono tabular-nums">{shipped.toFixed(0)}</td>
                  <td className="text-right">
                    <span className="font-mono tabular-nums">{received.toFixed(0)}</span>
                    {open > 0 && (
                      <span className="ml-2 badge bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 text-2xs">
                        {open.toFixed(0)} open
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action */}
      {t.status === 'draft' && (
        <button className="btn-primary w-full"
          disabled={shipMut.isPending}
          onClick={() => shipMut.mutate()}>
          {shipMut.isPending ? 'Shipping…' : 'Ship transfer'}
        </button>
      )}
      {t.status === 'in_transit' && (
        <ReceiveForm transferId={transferId} lines={t.lines} onChanged={onChanged} />
      )}
    </div>
  );
}

function ReceiveForm({ transferId, lines, onChanged }: { transferId: string; lines: any[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const [received, setReceived] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const l of lines) init[l.id] = parseFloat(l.quantity) - parseFloat(l.received_qty);
    return init;
  });

  const receiveMut = useMutation({
    mutationFn: () => inventoryApi.receiveTransfer(
      transferId,
      Object.entries(received)
        .filter(([, qty]) => qty > 0)
        .map(([lineId, receivedQty]) => ({ lineId, receivedQty })),
    ),
    onSuccess: (data) => {
      toast.success(`Received — status: ${data.status}, JE: ${data.journalEntryNumber ?? 'n/a'}`);
      qc.invalidateQueries({ queryKey: ['inv', 'transfer', transferId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const anyToReceive = Object.values(received).some((v) => v > 0);

  return (
    <div className="bg-canvas-subtle rounded-xl p-4 border border-ink-100">
      <div className="eyebrow mb-1 text-ink-500">Record receipt</div>
      <p className="text-xs text-ink-500 mb-3">
        Adjust each line's received quantity if different from shipped. Leave at 0 to keep a balance open.
      </p>
      <div className="space-y-2 mb-3">
        {lines.map((l: any) => {
          const open = parseFloat(l.quantity) - parseFloat(l.received_qty);
          return (
            <div key={l.id} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-ink-900 truncate">{l.product_name}</div>
                <div className="font-mono text-2xs text-ink-500">open: {open.toFixed(0)}</div>
              </div>
              <input
                type="number" min={0} max={open} step={1}
                className="input w-20 text-right tabular-nums"
                value={received[l.id] ?? 0}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(open, parseFloat(e.target.value) || 0));
                  setReceived((prev) => ({ ...prev, [l.id]: v }));
                }}
              />
            </div>
          );
        })}
      </div>
      <button
        className="btn-primary w-full"
        disabled={!anyToReceive || receiveMut.isPending}
        onClick={() => receiveMut.mutate()}
      >
        {receiveMut.isPending ? 'Receiving…' : 'Confirm receipt'}
      </button>
    </div>
  );
}

// ============================================================
// CreateTransferModal
// ============================================================
function CreateTransferModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<{ productId: string; productName?: string; sku?: string; quantity: number }>>([]);
  const [productSearch, setProductSearch] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const prodQuery = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () => inventoryApi.createTransfer({
      fromWarehouseId: fromId, toWarehouseId: toId,
      lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
      notes: notes || undefined,
    }),
    onSuccess: (data) => {
      toast.success(`Transfer ${data.transferNumber} created (draft). Ship when ready.`);
      onCreated(data.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = fromId && toId && fromId !== toId && lines.length > 0 && lines.every((l) => l.quantity > 0);
  const totalUnits = lines.reduce((a, l) => a + l.quantity, 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New transfer</h2>
      </div>

      <div className="px-6 pb-4 flex-shrink-0">
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
          <div>
            <label className="label">From</label>
            <select className="input" value={fromId} onChange={(e) => setFromId(e.target.value)}>
              <option value="">Select source…</option>
              {whQuery.data?.map((w: any) => (
                <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
              ))}
            </select>
          </div>
          <div className="pb-2 text-ink-400">
            <ArrowRight />
          </div>
          <div>
            <label className="label">To</label>
            <select className="input" value={toId} onChange={(e) => setToId(e.target.value)}>
              <option value="">Select destination…</option>
              {whQuery.data?.filter((w: any) => w.id !== fromId).map((w: any) => (
                <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
              ))}
            </select>
          </div>
        </div>
        {fromId && toId && fromId === toId && (
          <p className="text-xs text-danger mt-2">Source and destination must be different.</p>
        )}
      </div>

      <div className="px-6 pb-3">
        <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Delivery instructions, urgency, etc." />
      </div>

      <div className="px-6 flex-1 overflow-y-auto">
        <div className="eyebrow text-ink-500 mb-2">Lines ({lines.length})</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-ink-100 relative">
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="input pl-9 text-sm"
                placeholder="Search product to add…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </div>
            {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
              <div className="absolute left-3 right-3 top-full mt-1 bg-canvas-elevated border border-ink-200 rounded-xl shadow-lg max-h-60 overflow-y-auto z-20">
                {(prodQuery.data?.data ?? []).map((p: any) => (
                  <button
                    key={p.id}
                    className="w-full text-left px-3 py-2 hover:bg-canvas-subtle border-b border-ink-100 last:border-0 transition-colors"
                    onClick={() => {
                      setLines((prev) => [...prev, { productId: p.id, productName: p.name, sku: p.sku, quantity: 1 }]);
                      setProductSearch('');
                    }}
                  >
                    <div className="font-semibold text-ink-900 text-sm">{p.name}</div>
                    <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500">
              Search above to add products.
            </div>
          ) : (
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right w-24">Qty</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <div className="font-medium text-ink-900">{l.productName}</div>
                      {l.sku && <div className="font-mono text-2xs text-ink-500">{l.sku}</div>}
                    </td>
                    <td className="text-right">
                      <input
                        type="number" min="0.01" step="1"
                        className="input w-20 text-right tabular-nums"
                        value={l.quantity}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: v } : x));
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                        aria-label="Remove line"
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow text-ink-500">Total units</div>
          <div className="font-display font-bold text-xl text-ink-900 tabular-nums tracking-tight">
            {totalUnits > 0 ? totalUnits.toLocaleString() : '—'}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? 'Creating…' : 'Create transfer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function ArrowRight() { return <svg {...iconProps} width="14" height="14" className="text-ink-400"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>; }

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
